/**
 * Microphone capture for a hands-free voice session.
 *
 * Differs from the push-to-talk recorder in three ways that matter once the
 * loop re-arms itself:
 *
 * 1. One `getUserMedia` stream is held for the whole session. Re-acquiring per
 *    utterance costs hundreds of milliseconds and re-prompts on some browsers.
 * 2. The speech threshold is derived from a measured noise floor rather than a
 *    fixed level, so a noisy room does not read as continuous speech (which
 *    would never endpoint) and a quiet one does not swallow soft speech.
 * 3. It can watch for speech *while Tally is talking* so the user can interrupt,
 *    measured against what the microphone hears of Tally's own voice. Speech
 *    heard then is recorded from its onset, so if it becomes the next
 *    question its first words are kept.
 */

/** Time spent sampling the room before the first utterance. */
const CALIBRATION_MS = 400;
/** Level-metering cadence. */
const POLL_MS = 50;
/** Silence after speech onset that ends an utterance. */
const DEFAULT_SILENCE_HANG_MS = 1400;
/** Armed with nobody speaking - gives up so the session can wind down. */
const DEFAULT_NO_SPEECH_TIMEOUT_MS = 7000;
/** Hard cap on one utterance. */
const DEFAULT_MAX_UTTERANCE_MS = 25000;
/** Sustained speech required to interrupt playback. */
const BARGE_IN_SUSTAIN_MS = 400;
/**
 * Speech dips below the threshold between syllables. Only a quiet stretch this
 * long ends an interruption attempt; without it a spoken "stop" rarely held
 * above the threshold for the full sustain window.
 */
const BARGE_IN_GAP_MS = 200;
/** Speech is this much above the measured noise floor. */
const SPEECH_FACTOR = 3.5;
/**
 * Once speech has started it only counts as stopped below this share of the
 * onset threshold. Word endings and quieter words mid-sentence dip under the
 * onset level; with one threshold for both, the silence clock started while
 * the user was still talking and a short pause ended the turn.
 */
const SPEECH_RELEASE_RATIO = 0.5;
/**
 * Interrupting takes a higher bar: echo cancellation is imperfect, so Tally's
 * own voice leaks into the microphone and must not read as the user speaking.
 */
const BARGE_IN_FACTOR = 6;
/**
 * Over Tally's voice, measured against its average echo. That average counts
 * the gaps between words, so it sits well below Tally while it talks: 3x is
 * above Tally's own voice, but within reach of the user saying "stop". At 6x
 * the user could not interrupt at all.
 */
const ECHO_ONSET_FACTOR = 3;
/**
 * Once speech starts, Tally is turned down (DUCKED_VOLUME) and only a fifth of
 * its echo is left. The user's voice stays above this lower bar; an echo that
 * started an attempt falls below it, so the attempt ends and Tally comes back.
 */
const ECHO_SUSTAIN_FACTOR = 1.5;
/** Floor under the adaptive threshold, for rooms quiet enough to measure ~0. */
const MIN_SPEECH_LEVEL = 0.012;
/** Utterances shorter than this are a cough or a door, not a question. */
const MIN_UTTERANCE_MS = 350;

export interface VoiceCaptureCallbacks {
  /** An endpointed utterance, ready to transcribe. */
  onUtterance: (audio: Blob) => void;
  /** Armed, but nobody spoke before the timeout. */
  onNoSpeech: () => void;
  /** Sustained speech detected while watching during playback. */
  onBargeIn: () => void;
  /** Speech began while watching - it may or may not become a barge-in. */
  onSpeechStart?: () => void;
  /** That speech stopped before it became a barge-in. */
  onSpeechEnd?: () => void;
  /** Current input level, 0..1, for the overlay animation. */
  onLevel?: (level: number) => void;
  onError: (error: Error) => void;
}

export interface VoiceCaptureOptions {
  silenceHangMs?: number;
  noSpeechTimeoutMs?: number;
  maxUtteranceMs?: number;
}

type CaptureMode = "idle" | "armed" | "watching";

export class VoiceCapture {
  private stream: MediaStream | null = null;
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private recorder: MediaRecorder | null = null;
  private chunks: BlobPart[] = [];
  private pollTimer: ReturnType<typeof setInterval> | null = null;

  private mode: CaptureMode = "idle";
  private noiseFloor = 0;
  /**
   * What the microphone hears while Tally plays: the room plus Tally's own
   * voice leaking past echo cancellation. Barge-in is measured against this,
   * never endpointing, so Tally's voice cannot interrupt Tally.
   */
  private echoFloor = 0;
  private calibrating = true;
  private calibrationSamples: number[] = [];

  private speechStartedAt: number | null = null;
  private silenceStartedAt: number | null = null;
  private armedAt = 0;
  private bargeInStartedAt: number | null = null;
  /**
   * When the speech being recorded while watching began. Set from its onset
   * until it is either dropped (it stopped short of a barge-in) or taken over
   * by `arm`.
   */
  private heardAt: number | null = null;
  private bargeInQuietSince: number | null = null;

  private readonly silenceHangMs: number;
  private readonly noSpeechTimeoutMs: number;
  private readonly maxUtteranceMs: number;

  constructor(
    private readonly callbacks: VoiceCaptureCallbacks,
    options: VoiceCaptureOptions = {},
  ) {
    this.silenceHangMs = options.silenceHangMs ?? DEFAULT_SILENCE_HANG_MS;
    this.noSpeechTimeoutMs =
      options.noSpeechTimeoutMs ?? DEFAULT_NO_SPEECH_TIMEOUT_MS;
    this.maxUtteranceMs = options.maxUtteranceMs ?? DEFAULT_MAX_UTTERANCE_MS;
  }

  /** Acquires the microphone and measures the room. Call once per session. */
  async open(): Promise<void> {
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        // Without echo cancellation the session hears itself and barge-in
        // triggers on Tally's own voice.
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });

    const audioContext = new AudioContext();
    // Browsers hand back a suspended context when it was not created directly
    // inside the gesture; resuming is a no-op when it is already running.
    if (audioContext.state === "suspended") {
      await audioContext.resume().catch(() => {});
    }

    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 2048;
    const source = audioContext.createMediaStreamSource(this.stream);
    source.connect(analyser);

    this.audioContext = audioContext;
    this.analyser = analyser;
    this.source = source;
    this.calibrating = true;
    this.calibrationSamples = [];

    this.pollTimer = setInterval(() => this.poll(), POLL_MS);
  }

  /**
   * Starts recording and endpoints on silence.
   *
   * Speech already being recorded while watching - the user talking over
   * Tally, or starting as it finished - is kept as the start of the
   * utterance. Starting a fresh recording here lost those first words.
   */
  arm(): void {
    if (!this.stream || this.mode === "armed") return;

    const heardAt = this.heardAt;
    this.heardAt = null;
    if (heardAt === null || this.recorder === null) {
      this.dropRecorder();
      if (!this.startRecorder()) return;
    }

    this.mode = "armed";
    this.armedAt = Date.now();
    this.speechStartedAt = heardAt;
    this.silenceStartedAt = null;
  }

  /**
   * Stops recording without emitting, throwing the audio away - when the
   * session is torn down, and before watching for barge-in.
   */
  disarm(): void {
    this.dropRecorder();
    this.mode = "idle";
    this.heardAt = null;
    this.speechStartedAt = null;
    this.silenceStartedAt = null;
  }

  private startRecorder(): boolean {
    if (!this.stream) return false;
    // This recording's own list. A recorder hands over its last audio after
    // stop(), when the next recording may already have started; it must not
    // land in that one.
    const chunks: BlobPart[] = [];
    this.chunks = chunks;
    try {
      this.recorder = new MediaRecorder(this.stream);
    } catch (error) {
      this.callbacks.onError(
        error instanceof Error ? error : new Error("Recorder failed to start"),
      );
      return false;
    }

    this.recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunks.push(event.data);
    };
    this.recorder.onstop = () => this.emitUtterance();
    this.recorder.start();
    return true;
  }

  /** Stops the recorder, if any, and throws its audio away. */
  private dropRecorder(): void {
    if (this.recorder && this.recorder.state !== "inactive") {
      this.recorder.ondataavailable = null;
      this.recorder.onstop = null;
      try {
        this.recorder.stop();
      } catch {
        // Already stopping.
      }
    }
    this.recorder = null;
    this.chunks = [];
  }

  /**
   * Listens for the user talking over Tally. Only speech is recorded, from
   * its onset, so it is not lost if it turns into the next question.
   */
  watchForBargeIn(): void {
    this.disarm();
    this.mode = "watching";
    this.bargeInStartedAt = null;
    this.bargeInQuietSince = null;
  }

  /** Releases the microphone and every audio node. */
  close(): void {
    this.disarm();
    this.mode = "idle";

    if (this.pollTimer !== null) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
    this.source?.disconnect();
    this.source = null;
    this.analyser = null;
    this.audioContext?.close().catch(() => {});
    this.audioContext = null;
    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = null;
  }

  private currentLevel(): number {
    if (!this.analyser) return 0;
    const data = new Uint8Array(this.analyser.fftSize);
    this.analyser.getByteTimeDomainData(data);

    let sum = 0;
    for (let i = 0; i < data.length; i++) {
      const value = (data[i] - 128) / 128;
      sum += value * value;
    }
    return Math.sqrt(sum / data.length);
  }

  private speechThreshold(factor: number): number {
    return Math.max(this.noiseFloor * factor, MIN_SPEECH_LEVEL);
  }

  private poll(): void {
    const level = this.currentLevel();
    this.callbacks.onLevel?.(level);

    if (this.calibrating) {
      this.calibrationSamples.push(level);
      if (this.calibrationSamples.length * POLL_MS >= CALIBRATION_MS) {
        // Median, not mean: a cough during calibration should not set the floor.
        const sorted = [...this.calibrationSamples].sort((a, b) => a - b);
        this.noiseFloor = sorted[Math.floor(sorted.length / 2)] ?? 0;
        this.echoFloor = this.noiseFloor;
        this.calibrating = false;
      }
      return;
    }

    switch (this.mode) {
      case "armed":
        this.pollArmed(level);
        break;
      case "watching":
        this.pollWatching(level);
        break;
      default:
        // Idle is between turns, while Tally is acknowledging or answering.
        // Learning Tally's voice into the noise floor would raise the speech
        // threshold a little more every turn; it belongs in the echo floor.
        // Not while a barge-in is still recording: that is the user.
        if (this.recorder === null) this.trackEchoFloor(level);
        break;
    }
  }

  /** Slow EMA over quiet samples, so the floor follows the room, not speech. */
  private trackNoiseFloor(level: number): void {
    if (level < this.speechThreshold(SPEECH_FACTOR)) {
      this.noiseFloor = this.noiseFloor * 0.95 + level * 0.05;
    }
  }

  /** Follows every sample while Tally plays, speech-like leakage included. */
  private trackEchoFloor(level: number): void {
    this.echoFloor = this.echoFloor * 0.95 + level * 0.05;
  }

  /** Lower once an attempt has started, as Tally has been turned down. */
  private bargeInThreshold(attempting: boolean): number {
    const echoFactor = attempting ? ECHO_SUSTAIN_FACTOR : ECHO_ONSET_FACTOR;
    return Math.max(
      this.noiseFloor * BARGE_IN_FACTOR,
      this.echoFloor * echoFactor,
      MIN_SPEECH_LEVEL,
    );
  }

  private pollArmed(level: number): void {
    const now = Date.now();
    const threshold = this.speechThreshold(SPEECH_FACTOR);

    if (this.speechStartedAt === null) {
      if (level >= threshold) {
        this.speechStartedAt = now;
        this.silenceStartedAt = null;
        return;
      }
      this.trackNoiseFloor(level);
      if (now - this.armedAt >= this.noSpeechTimeoutMs) {
        this.disarm();
        this.callbacks.onNoSpeech();
      }
      return;
    }

    if (now - this.speechStartedAt >= this.maxUtteranceMs) {
      this.stopAndEmit();
      return;
    }

    // Never below the minimum: the analyser reads in steps of 1/128, so any
    // faint noise after the user stops measures ~0.008, and a lower bar would
    // never see the silence that ends the turn.
    const release = Math.max(
      threshold * SPEECH_RELEASE_RATIO,
      MIN_SPEECH_LEVEL,
    );
    if (level < release) {
      if (this.silenceStartedAt === null) {
        this.silenceStartedAt = now;
      } else if (now - this.silenceStartedAt >= this.silenceHangMs) {
        this.stopAndEmit();
      }
    } else {
      this.silenceStartedAt = null;
    }
  }

  private pollWatching(level: number): void {
    const now = Date.now();
    if (level >= this.bargeInThreshold(this.bargeInStartedAt !== null)) {
      this.bargeInQuietSince = null;
      if (this.bargeInStartedAt === null) {
        this.bargeInStartedAt = now;
        this.heardAt = now;
        this.startRecorder();
        this.callbacks.onSpeechStart?.();
      } else if (now - this.bargeInStartedAt >= BARGE_IN_SUSTAIN_MS) {
        this.bargeInStartedAt = null;
        this.mode = "idle";
        this.callbacks.onBargeIn();
      }
      return;
    }

    if (this.bargeInStartedAt === null) {
      // Nobody is trying to interrupt, so this is Tally and the room.
      this.trackEchoFloor(level);
      return;
    }
    if (this.bargeInQuietSince === null) {
      this.bargeInQuietSince = now;
    } else if (now - this.bargeInQuietSince >= BARGE_IN_GAP_MS) {
      this.bargeInStartedAt = null;
      this.bargeInQuietSince = null;
      // A cough, not the next question.
      this.dropRecorder();
      this.heardAt = null;
      this.callbacks.onSpeechEnd?.();
    }
  }

  private stopAndEmit(): void {
    this.mode = "idle";
    if (this.recorder && this.recorder.state !== "inactive") {
      try {
        this.recorder.stop(); // onstop -> emitUtterance
        return;
      } catch {
        // Fall through and emit what we have.
      }
    }
    this.emitUtterance();
  }

  private emitUtterance(): void {
    const spokenMs =
      this.speechStartedAt === null ? 0 : Date.now() - this.speechStartedAt;
    const chunks = this.chunks;
    this.chunks = [];
    this.recorder = null;
    this.speechStartedAt = null;
    this.silenceStartedAt = null;

    if (chunks.length === 0 || spokenMs < MIN_UTTERANCE_MS) {
      // Too short to be a question - treat as if nobody spoke rather than
      // spending a transcription call on it.
      this.callbacks.onNoSpeech();
      return;
    }

    this.callbacks.onUtterance(new Blob(chunks, { type: "audio/webm" }));
  }
}
